import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Callout, Ext, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Aviso sobre Datos Biométricos y Datos de Salud del Consumidor (español).
// Mantener en paridad con biometric.en.tsx: mismos ids, mismos plazos.

export const biometricEs: LegalDoc = {
  lang: 'es',
  title: 'Aviso sobre Datos Biométricos y Datos de Salud del Consumidor',
  updatedLabel: `Última actualización: ${entity.effectiveDate}`,
  contentsLabel: 'Contenido',
  altHref: legalPaths.biometric,
  altLabel: 'View in English',
  sections: [
    {
      id: 'why',
      heading: '1. Por qué es un documento aparte',
      body: (
        <>
          <P>
            Tribys ofrece verificación de identidad opcional. Si la usas, tratamos
            una imagen de tu rostro, lo que constituye información biométrica y está
            regulado de forma específica por varios estados de EE. UU. Illinois nos
            obliga a publicar una política escrita que establezca cuánto tiempo
            conservamos la información biométrica y cómo la destruimos. Washington
            exige que el aviso relativo a este tipo de datos se publique por separado
            de nuestro aviso de privacidad general. Este es ese documento.
          </P>
          <P>
            Todo lo demás sobre cómo Tribys trata tus datos está en el{' '}
            <a href={legalPaths.privacyEs}>Aviso de Privacidad</a>. Este aviso lo
            complementa y, en materia de datos biométricos y de salud del consumidor,
            prevalece.
          </P>
          <Callout>
            <strong>La verificación de identidad es totalmente opcional.</strong>{' '}
            Puedes usar Tribys sin ella: la pantalla de verificación tiene un botón
            para omitirla. Si la omites, nada de este aviso te aplica, porque nunca
            recabamos información biométrica alguna sobre ti.
          </Callout>
        </>
      ),
    },
    {
      id: 'what',
      heading: '2. Qué recabamos y qué no',
      body: (
        <>
          <P>Si decides verificar tu identidad, el proceso recaba:</P>
          <Ul
            items={[
              'Una imagen de tu identificación oficial.',
              'Un video selfie breve, para confirmar que hay una persona presente en vivo (prueba de «vitalidad»).',
              'Una única imagen fija de referencia de tu rostro, extraída de esa comprobación.',
              'El nombre, la fecha de nacimiento y el género impresos en tu documento.',
              'Qué tipo de documento usaste, por ejemplo una credencial de identidad o un pasaporte.',
            ]}
          />
          <P>
            <strong>Lo que Tribys conserva</strong> es deliberadamente limitado: el
            nombre, la fecha de nacimiento y el género verificados, el tipo de
            documento que usaste, una imagen fija de referencia de tu rostro y la
            fecha en que otorgaste tu consentimiento. Nada más.
          </P>
          <P>
            <strong>Lo que Tribys nunca almacena:</strong> la imagen de tu documento
            de identidad, <strong>su número</strong>, ningún número personal o de
            identificación nacional que contenga, el video de verificación ni
            ninguna estimación de edad producida durante la comprobación. Todo ello
            lo gestiona nuestro encargado de verificación y no se conserva en
            nuestros sistemas.
          </P>
          <P>
            Por separado, cuando subes una foto de perfil <em>y</em> estás
            verificado, comparamos el rostro de esa foto con tu imagen de referencia
            almacenada y conservamos la puntuación numérica de similitud de esa
            comparación. No creamos ni almacenamos una plantilla facial reutilizable
            y no inscribimos tu rostro en ninguna base de datos facial consultable:
            cada comparación es puntual, entre dos imágenes.
          </P>
        </>
      ),
    },
    {
      id: 'why-we-use-it',
      heading: '3. Para qué lo usamos',
      body: (
        <>
          <P>
            Tribys reúne a personas desconocidas en un mismo lugar. La verificación
            biométrica se usa exactamente para dos fines, ambos relacionados con eso:
          </P>
          <Ul
            items={[
              <><strong>Confirmar una sola vez que eres una persona real.</strong> Es lo que otorga la insignia de verificación, permite crear grupos solo para personas verificadas y permite crear un código de referido.</>,
              <><strong>Evitar que alguien ponga el rostro de otra persona en un perfil verificado.</strong> Por eso las fotos de perfil posteriores se comparan con tu imagen de referencia. Sin ello la insignia no valdría nada: cualquiera podría verificarse con su propia identificación y luego sustituir la foto por la de otra persona.</>,
            ]}
          />
          <P>
            No usamos la información biométrica para nada más. Ni para publicidad, ni
            para analítica, ni para entrenar modelos, ni para seguirte por la
            aplicación, ni para identificarte en las fotos de otros miembros.
          </P>
        </>
      ),
    },
    {
      id: 'consent',
      heading: '4. Consentimiento',
      body: (
        <>
          <P>
            Solicitamos tu consentimiento por escrito antes de captar cualquier
            información biométrica. En la pantalla de verificación debes marcar
            activamente una casilla en la que confirmas que consientes la recolección
            y el tratamiento de tus datos biométricos y de tu identificación oficial.
            Esa marca constituye tu autorización escrita, registramos la fecha y hora
            en que la otorgaste, y puedes consultar esa fecha en la exportación de tus
            datos.
          </P>
          <P>
            Puedes negarte. La verificación es opcional, la pantalla permite omitirla
            y negarte no restringe tu uso de Tribys más allá de las tres funciones
            para personas verificadas antes señaladas.
          </P>
          <P>
            <strong>Puedes revocar tu consentimiento en cualquier momento</strong>{' '}
            escribiendo a <Tok value={entity.privacyEmail} /> o eliminando tu cuenta.
            Al revocarlo destruimos tu imagen de referencia y tu registro de
            verificación, instruimos a nuestro encargado para que elimine su copia y
            retiramos tu estado de verificación. La revocación no deshace las
            comparaciones ya realizadas.
          </P>
        </>
      ),
    },
    {
      id: 'who',
      heading: '5. Quién más participa',
      body: (
        <>
          <P>
            Dos proveedores tratan información biométrica por nuestra cuenta. Ambos
            actúan únicamente conforme a nuestras instrucciones y no pueden usar tus
            datos para fines propios.
          </P>
          <Ul
            items={[
              <><strong>Didit</strong> realiza la comprobación de identidad: lee tu documento y ejecuta la prueba de vitalidad. Sus términos están en{' '}<Ext href="https://didit.me/terms/privacy-policy/">didit.me/terms/privacy-policy</Ext>.</>,
              <><strong>Amazon Web Services</strong> realiza la comparación facial entre una foto de perfil nueva y tu imagen de referencia, mediante Amazon Rekognition, y almacena tu imagen de referencia.</>,
            ]}
          />
          <P>
            No divulgamos, redivulgamos ni difundimos de ningún otro modo información
            biométrica a terceros, salvo que tú lo consientas o que una citación,
            orden judicial u otro requerimiento legal nos obligue.
          </P>
        </>
      ),
    },
    {
      id: 'retention',
      heading: '6. Calendario de conservación y destrucción',
      body: (
        <>
          <P>
            Esta sección constituye nuestro calendario de conservación y nuestras
            directrices de destrucción, según lo exige la legislación de Illinois.
          </P>
          <H3>Documento de identidad y video de verificación</H3>
          <P>
            Los conserva únicamente nuestro encargado de verificación, nunca
            nosotros, y se <strong>eliminan a los 6 meses</strong>.
          </P>
          <H3>Tu imagen facial de referencia y las puntuaciones de comparación</H3>
          <P>Las destruimos en el momento que ocurra primero de entre:</P>
          <Ul
            items={[
              'cuando eliminas tu cuenta: la imagen se elimina de nuestro almacenamiento como parte del proceso de supresión descrito en el Aviso de Privacidad;',
              'cuando revocas tu consentimiento biométrico;',
              'cuando se cumple la finalidad, es decir, cuando dejas de ser un miembro verificado;',
              'tres años después de tu última interacción con Tribys.',
            ]}
          />
          <P>
            El límite máximo de tres años aplica en todo caso, incluso a una cuenta
            que simplemente queda inactiva y nunca se elimina. Para residentes de
            Texas destruimos además los identificadores biométricos dentro del año
            siguiente a la extinción de la finalidad que motivó su recolección.
          </P>
          <H3>Nombre, fecha de nacimiento y género verificados</H3>
          <P>
            No son información biométrica, pero provienen de tu documento, así que
            los tratamos conjuntamente: se conservan mientras tu cuenta esté
            verificada y se borran al eliminar tu cuenta o al revocar tu
            consentimiento.
          </P>
          <H3>Cómo destruimos la información</H3>
          <P>
            Destruir significa la eliminación permanente del objeto almacenado y de
            su registro en la base de datos, no archivarlo ni anonimizarlo. La
            eliminación se ejecuta automáticamente y además instruimos a nuestro
            encargado para que elimine su copia mediante API. No conservamos copias de
            respaldo de información biométrica fuera del ciclo normal y de corta
            duración de respaldo de los propios sistemas de almacenamiento.
          </P>
        </>
      ),
    },
    {
      id: 'no-sale',
      heading: '7. Nunca vendemos ni lucramos con información biométrica',
      body: (
        <Callout>
          <strong>
            Tribys no vende, arrienda, comercia ni obtiene lucro de tus
            identificadores o información biométrica, ni de tus datos de salud del
            consumidor, y no lo hará.
          </strong>{' '}
          No existe circunstancia en la que lo haríamos. Tampoco los usamos para
          publicidad, y no operamos ningún servicio de publicidad o analítica.
        </Callout>
      ),
    },
    {
      id: 'security',
      heading: '8. Cómo la protegemos',
      body: (
        <P>
          Protegemos la información biométrica aplicando, como mínimo, el mismo
          estándar de cuidado que aplicamos a otra información confidencial y
          sensible, de forma igual o más protectora. Tu imagen de referencia se
          almacena en almacenamiento privado en la nube, cifrado en reposo, no
          listable públicamente y que no se sirve desde nuestra red pública de
          contenido. Solo el servicio del backend que realiza la comparación puede
          leerla, mediante credenciales federadas de corta duración en lugar de claves
          de acceso almacenadas. Todas las transferencias usan TLS 1.2 o superior y el
          acceso se limita a los servicios que lo necesitan.
        </P>
      ),
    },
    {
      id: 'health',
      heading: '9. Datos de salud del consumidor',
      body: (
        <>
          <P>
            La My Health My Data Act de Washington, y leyes similares en Nevada y
            Connecticut, emplean una definición amplia de «datos de salud del
            consumidor» que incluye los datos biométricos. Sobre esa base, la imagen
            facial de referencia y las puntuaciones de comparación descritas arriba
            son los datos de salud del consumidor que tratamos. Los obtenemos de ti,
            a través del flujo de verificación, para los fines de la sección 3, y solo
            los compartimos con los dos encargados nombrados en la sección 5.
          </P>
          <P>
            Queremos ser igual de claros sobre lo que <em>no</em> hacemos:
          </P>
          <Ul
            items={[
              'No te pedimos ninguna información de salud y Tribys no tiene ningún campo para ello.',
              'No inferimos tu estado de salud, física o mental, a partir de nada de lo que hagas en la aplicación.',
              'No analizamos tus intereses, los grupos a los que te unes ni los lugares a los que vas para derivar información sobre salud, padecimientos, tratamientos, salud reproductiva o sexual, o atención de afirmación de género, aunque un grupo o un interés que elijas pudiera tocar alguno de esos temas.',
              'No usamos la imagen facial de referencia para estimar o inferir nada sobre tu cuerpo o tu salud. Se usa solo para comprobar si dos fotos muestran a la misma persona. Nuestro encargado de verificación sí produce una estimación de edad durante su comprobación; nosotros no la almacenamos ni la usamos.',
              'No vendemos datos de salud del consumidor, y para hacerlo necesitaríamos tu autorización escrita por separado, que nunca te pediremos.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'rights',
      heading: '10. Tus derechos',
      body: (
        <>
          <P>Sea cual sea tu lugar de residencia, puedes:</P>
          <Ul
            items={[
              'pedirnos que confirmemos si tenemos datos biométricos o de salud del consumidor sobre ti, y obtener una copia, incluida la lista de con quién los compartimos;',
              'revocar tu consentimiento, según la sección 4;',
              'solicitar su eliminación: los eliminaremos y trasladaremos tu solicitud a los encargados de la sección 5;',
              'presentar una queja ante nosotros y ante la autoridad competente de tu jurisdicción.',
            ]}
          />
          <P>
            Escribe a <Tok value={entity.privacyEmail} />. Es gratuito, no te
            trataremos de forma distinta por solicitarlo y respondemos en un plazo de
            45 días. Eliminar tu cuenta desde Ajustes logra la misma eliminación, de
            inmediato y sin necesidad de solicitarla.
          </P>
          <P>
            Residentes de Illinois: la BIPA les otorga una acción privada, y nada en
            este aviso ni en nuestros Términos la limita.
          </P>
        </>
      ),
    },
    {
      id: 'contact',
      heading: '11. Contacto',
      body: (
        <P>
          <Tok value={entity.legalName} />
          <br />
          <Tok value={entity.address} />
          <br />
          <Tok value={entity.privacyEmail} />
        </P>
      ),
    },
  ],
};
